// React/finance-app/src/AdvicePasswordManagement.js

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';

// Hooks 
import usePasswordFetch from './features/passwords/hooks/usePasswordFetch';
import usePasswordDerived from './features/passwords/hooks/usePasswordDerived';
import usePasswordEditing from './features/passwords/hooks/usePasswordEditing';
import usePasswordBulk from './features/passwords/hooks/usePasswordBulk';
import useRealtimeUpdates from './features/passwords/hooks/useRealtimeUpdates';
import { useDebouncedCallback } from './hooks/useDebounce';

// Components
import {
  BulkForm,
  Table,
  SearchBar,
  Toast,
} from './features/passwords/components';
import useSiteRequests from './features/site-requests/useSiteRequests';
import SiteRequestsPanel from './features/site-requests/SiteRequestsPanel';

// API
import api, {
  saveAdvicePasswordProxy,
  fetchUniqueSites,
  getSessionStampWho,
  getSessionStampedAs,
} from './api';
import { usePortActivationStatus, PortActivationBadge } from './shared/portActivation';
//
function AdvicePasswordManagement() {
  // ----------------------------------------------------------------
  // Local UI state (must be before usePasswordFetch for skip/limit/searchTerm)
  // ----------------------------------------------------------------
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState(null); // 'subscribed' | 'onboarded' | null
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 500;

  // Debounce search so we only fetch after user stops typing (not on every keystroke)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Use when setting search from code (e.g. after bulk create) so fetch runs immediately
  const setSearchTermImmediate = useCallback((value) => {
    setSearchTerm(value);
    setDebouncedSearchTerm(value);
  }, []);

  // ----------------------------------------------------------------
  // Fetch (advice=true → use /advice-passwords/ endpoints, server-side pagination & search)
  // ----------------------------------------------------------------
  const { passwords, ports, usersMap, totalCount, loading, error, refetch } = usePasswordFetch({
    advice: true,
    skip: (currentPage - 1) * itemsPerPage,
    limit: itemsPerPage,
    searchTerm: debouncedSearchTerm,
  });

  const hasLoadedOnce = useRef(false);
  useEffect(() => {
    if (!loading) hasLoadedOnce.current = true;
  }, [loading]);

  const [expandedUserCode, setExpandedUserCode] = useState(null);
  const [autoExpandAll, setAutoExpandAll] = useState(false);
  const [fullRowsByUserCode, setFullRowsByUserCode] = useState({});
  const [notesOpenMap, setNotesOpenMap] = useState({});

  const { portStatus, refresh: refreshPortActivation } = usePortActivationStatus({
    scope: 'staff',
  });
  const [portCountdowns, setPortCountdowns] = useState({});
  const [sites, setSites] = useState([]);

  // Optimistic multilogin overrides: { [entryId]: bool, [entryId + '_saving']: bool }
  const [multiloginOverrides, setMultiloginOverrides] = useState({});
  const [mlEnvironmentOverrides, setMlEnvironmentOverrides] = useState({});
  // Optimistic persona / self-transfer fields keyed by password id
  const [personaSelfOverrides, setPersonaSelfOverrides] = useState({});

  // ----------------------------------------------------------------
  // Derived data - server-side pagination/search; only sort client-side
  // ----------------------------------------------------------------
  const {
    userCodeMainEntries,
    sortedUserCodeMainEntries,
  } = usePasswordDerived({
    passwords,
    users: usersMap,
    ports,
    searchTerm: '',
    sortField,
    sortDirection,
    currentPage: 1,
    itemsPerPage: 9999,
  });

  // When a row is expanded, use full rows for that user if we fetched them; otherwise use derived allEntries.
  // When a search is active, skip the full-row cache — it contains unfiltered rows and would override
  // the server-filtered results, showing every email even when only one matches the search.
  const paginatedEntries = useMemo(() => {
    return sortedUserCodeMainEntries.map((entry) => {
      const fullRows = debouncedSearchTerm ? null : fullRowsByUserCode[entry.userCode];
      if (fullRows?.length) {
        // Enrich raw API rows with phone_number from ports
        // (fullRowsByUserCode contains unenriched rows straight from the API)
        const enrichedFullRows = fullRows.map((row) => {
          const uc = String(row.user_code || '').trim().toLowerCase();
          const em = String(row.email || '').trim().toLowerCase();
          const matchingPort = ports.find(
            (pt) =>
              (pt.user_code || '').trim().toLowerCase() === uc &&
              (pt.user_email || '').trim().toLowerCase() === em
          );
          const perUser = usersMap[row.user_code] || {};
          const base = perUser.base || {};
          const emailData = perUser[row.email] || {};
          return {
            ...row,
            phone_number: matchingPort?.assigned_number || '',
            full_name:
              emailData.email_specific_name ||
              base.full_name ||
              '',
            address:
              emailData.email_specific_address ||
              base.address ||
              '',
            dob:
              emailData.email_specific_dob ||
              base.dob ||
              '',
            notes: base.notes || '',
            slack_id: base.slack_user_id || '',
          };
        });
  
        const uniqueEmails = new Set(
          enrichedFullRows.map((e) => (e.email || '').trim().toLowerCase()).filter(Boolean)
        );
        return {
          ...entry,
          allEntries: enrichedFullRows,
          totalEntries: enrichedFullRows.length,
          emailsCount: uniqueEmails.size,
        };
      }
      return entry;
    });
  }, [sortedUserCodeMainEntries, fullRowsByUserCode, ports, usersMap, debouncedSearchTerm]);

  // Count unique user codes with active subscription from loaded data
  const subscribedCount = useMemo(() => {
    const seen = new Set();
    passwords.forEach((p) => {
      if (p.subscription_status === 'active' && p.user_code) {
        seen.add(p.user_code.trim());
      }
    });
    return seen.size;
  }, [passwords]);

  // Auto-expand if exactly one user appears
  React.useEffect(() => {
    if (userCodeMainEntries.length === 1) {
      setExpandedUserCode(userCodeMainEntries[0].userCode);
    }
  }, [userCodeMainEntries]);

  // When search matches exactly one user, expand that user and all email groups
  React.useEffect(() => {
    const term = (debouncedSearchTerm || '').trim();
    if (!term) {
      setAutoExpandAll(false);
      return;
    }
  // Expand if exactly one user code came back (regardless of which field matched)
  if (userCodeMainEntries.length === 1) {
    setExpandedUserCode(userCodeMainEntries[0].userCode);
      setAutoExpandAll(true);
    } else {
      setAutoExpandAll(false);
    }
  }, [debouncedSearchTerm, userCodeMainEntries]);

  // Fetch all rows for a user when expanding (so we show full list without changing search)
  const fetchFullRowsForUser = useCallback(async (userCode) => {
    if (!userCode) return;
    try {
      const res = await api.get('/advice-passwords/', {
        params: { user_code: userCode.trim(), limit: 500, skip: 0 },
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setFullRowsByUserCode((prev) => ({ ...prev, [userCode]: rows }));
    } catch (err) {
      console.error('Failed to fetch full rows for', userCode, err);
    }
  }, []);

  // When user clicks to expand: load that user's rows in background and expand all email groups (no search change)
  const setExpandedUserCodeWithAutoExpand = useCallback(
    (userCode) => {
      setExpandedUserCode(userCode);
      setAutoExpandAll(!!userCode);
      if (userCode) {
        fetchFullRowsForUser(userCode);
      }
    },
    [fetchFullRowsForUser]
  );

  // Fetch sites on mount
  React.useEffect(() => {
    fetchUniqueSites()
      .then(setSites)
      .catch(() => setSites([]));
  }, []);

  const { submitRequest: submitSiteRequest } = useSiteRequests({ mine: false, status: 'pending' });
  const handleSitesChanged = useCallback(() => {
    refetch();
    fetchUniqueSites().then(setSites).catch(() => {});
  }, [refetch]);

  // ----------------------------------------------------------------
  // Editing logic (no multilogin on this page)
  // ----------------------------------------------------------------
  const {
    // feedback
    error: editError,
    toastMessage,
    showToast,

    // child editing
    editingId,
    editDraft,
    changeEditField,
    startEditingRow,
    cancelEditingRow,
    saveEditingRow,
    saveAndApplyAll, // applies base fields to all emails (no multilogin involved)

    // address editor
    showAddressEditor,
    setShowAddressEditor,
    addressParts,
    setAddressParts,
    parseAddressIntoParts,
    handleAddressSave,

    // child create/delete
    addingNewRecord,
    setAddingNewRecord,
    newRecordUserCode,
    setNewRecordUserCode,
    newRecordDraft,
    setNewRecordDraft,
    createNewRecord,
    deleteRecord,

    // main row editing
    editingMainRow,
    setEditingMainRow,
    mainRowEditDraft,
    setMainRowEditDraft,
    startEditingMainRow,
    saveMainRow,

    // notes
    saveNotes,
  } = usePasswordEditing({
    resourcePrefix: '/advice-passwords',
    refresh: refetch,
    withMultilogin: true,
    onNewRecordSuccess: (userCode) => {
      setSearchTermImmediate(userCode);
      setCurrentPage(1);
      setExpandedUserCode(userCode);
      setAutoExpandAll(true);
      // Invalidate stale expanded-row cache so memoized rows don't mask fresh refetch data
      setFullRowsByUserCode((prev) => {
        const next = { ...prev };
        delete next[userCode];
        return next;
      });
      // Refetch and repopulate this user's expanded rows immediately
      refetch();
      fetchFullRowsForUser(userCode);
    },
  });

  const handleToggleMultilogin = useCallback(async (entry, newValue) => {
    const id = entry.id;
    // Optimistic update
    setMultiloginOverrides((prev) => ({ ...prev, [id]: newValue, [`${id}_saving`]: true }));
    try {
      await api.put(`/advice-passwords/${id}`, {
        id,
        user_code: entry.user_code,
        email: entry.email,
        site: entry.site,
        password: entry.password,
        main: entry.main,
        multilogin: newValue,
      });
      // Clear saving flag, keep optimistic value until next refetch
      setMultiloginOverrides((prev) => {
        const next = { ...prev };
        delete next[`${id}_saving`];
        return next;
      });
    } catch (err) {
      console.error('Failed to toggle multilogin:', err);
      // Revert on failure
      setMultiloginOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        delete next[`${id}_saving`];
        return next;
      });
      showToast('✗ Failed to update multilogin');
    }
  }, [showToast]);

  const handleToggleMlEnvironment = useCallback(async (entry, newValue) => {
    const id = entry.id;
    setMlEnvironmentOverrides((prev) => ({ ...prev, [id]: newValue }));
    try {
      await api.put(`/advice-passwords/${id}`, {
        id,
        user_code: entry.user_code,
        email: entry.email,
        site: entry.site,
        password: entry.password,
        main: entry.main,
        ml_environment: newValue ?? '',  // send "" instead of null so backend knows to clear
      });
    } catch (err) {
      console.error('Failed to set ML environment:', err);
      setMlEnvironmentOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      showToast('✗ Failed to update ML environment');
    }
  }, [showToast]);

  const patchPersonaSelfOverride = useCallback((sourceEntry, patch) => {
    const email = (sourceEntry?.email || '').trim().toLowerCase();
    const uc = sourceEntry?.user_code || '';
    const matches = (row) =>
      (row?.user_code || '') === uc &&
      (row?.email || '').trim().toLowerCase() === email;

    setFullRowsByUserCode((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = (next[key] || []).map((row) =>
          matches(row) ? { ...row, ...patch } : row
        );
      });
      return next;
    });

    setPersonaSelfOverrides((prevOverrides) => {
      const nextOverrides = { ...prevOverrides };
      const matchedIds = new Set();
      Object.values(fullRowsByUserCode || {}).forEach((rows) => {
        (rows || []).forEach((row) => {
          if (matches(row) && row.id != null) matchedIds.add(row.id);
        });
      });
      (passwords || []).forEach((row) => {
        if (matches(row) && row.id != null) matchedIds.add(row.id);
      });
      if (matchedIds.size === 0 && sourceEntry?.id != null) {
        matchedIds.add(sourceEntry.id);
      }
      matchedIds.forEach((id) => {
        nextOverrides[id] = { ...(nextOverrides[id] || {}), ...patch };
      });
      return nextOverrides;
    });
  }, [fullRowsByUserCode, passwords]);

  const handlePersonaCheckSubmit = useCallback(
    async (entry, { answers }) => {
      const stampedAs = getSessionStampedAs();
      const stampedBy = getSessionStampWho();
      const res = await api.put(`/advice-passwords/${entry.id}`, {
        persona_check_triggered: true,
        persona_check_stamped_as: stampedAs,
        persona_check_stamped_by: stampedBy,
        persona_check_answers: answers,
      });
      const data = res?.data || {};
      patchPersonaSelfOverride(entry, {
        persona_check_triggered: true,
        persona_check_triggered_on:
          data.persona_check_triggered_on || new Date().toISOString(),
        persona_check_stamped_as: data.persona_check_stamped_as || stampedAs,
        persona_check_stamped_by: data.persona_check_stamped_by || stampedBy,
        persona_check_answers: answers,
      });
      showToast('✓ Persona check saved');
    },
    [patchPersonaSelfOverride, showToast]
  );

  const handlePersonaCheckClear = useCallback(
    async (entry) => {
      await api.put(`/advice-passwords/${entry.id}`, {
        persona_check_triggered: false,
      });
      patchPersonaSelfOverride(entry, {
        persona_check_triggered: false,
        persona_check_triggered_on: null,
        persona_check_stamped_as: null,
        persona_check_stamped_by: null,
        persona_check_answers: null,
      });
      showToast('✓ Persona check cleared');
    },
    [patchPersonaSelfOverride, showToast]
  );

  const handleSelfTransferToggle = useCallback(
    async (entry, checked) => {
      const res = await api.put(`/advice-passwords/${entry.id}`, {
        self_manage_transfer: checked,
      });
      const data = res?.data || {};
      patchPersonaSelfOverride(entry, {
        self_manage_transfer: checked,
        self_manage_transfer_since: checked
          ? data.self_manage_transfer_since || new Date().toISOString()
          : null,
      });
      showToast(checked ? '✓ Self transfer enabled' : '✓ Self transfer disabled');
    },
    [patchPersonaSelfOverride, showToast]
  );

  // ----------------------------------------------------------------
  // Bulk form logic (advice)
  // ----------------------------------------------------------------
  const {
    showNewPasswordForm,
    setShowNewPasswordForm,
    error: bulkError,
    toastMessage: bulkToast,
    newPasswordEntries,
    handleNewPasswordFieldChange,
    addNewPasswordEntry,
    removePasswordEntry,
    resetNewPasswordForm,
    invalidRows,
    hasValidationIssues,
    handleCreateNewPasswords,
  } = usePasswordBulk({
    resourcePrefix: '/advice-passwords',
    refresh: refetch,
    onSuccess: (createdEntries) => {
      const userCode = createdEntries?.[0]?.user_code;

      if (userCode) {
        setSearchTermImmediate(userCode);
        setCurrentPage(1);
        setExpandedUserCode(userCode);
        setAutoExpandAll(true);
        // Invalidate stale expanded-row cache so new rows appear immediately
        setFullRowsByUserCode((prev) => {
          const next = { ...prev };
          delete next[userCode];
          return next;
        });
        fetchFullRowsForUser(userCode);
      }

      // Immediate refetch — needed when search term didn't change (same userCode already in box)
      refetch();
    },
  });

  // ----------------------------------------------------------------
  // Realtime updates (debounced: only on relevant events)
  // ----------------------------------------------------------------
  // Debounced refetch - max once per 3 seconds
  const debouncedRefetch = useDebouncedCallback(() => {
    console.log('Advice: Debounced refetch triggered');
    refetch();
  }, 3000);

  useRealtimeUpdates(
    {
      onPassword: () => {
        console.log('Advice: Password update received, debouncing refetch...');
        debouncedRefetch();
      },
      onUser: () => {
        console.log('Advice: User update received, debouncing refetch...');
        debouncedRefetch();
      },
      // Don't refetch on port updates - not relevant to advice password management
    },
    [debouncedRefetch]
  );

  // ----------------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------------
  const toggleNotes = useCallback((userCode) => {
    if (expandedUserCode !== userCode) {
      setExpandedUserCode(userCode);
    }
    setNotesOpenMap((prev) => ({ ...prev, [userCode]: !prev[userCode] }));
  }, [expandedUserCode]);

  const handleSortBySubscribed = useCallback((e) => {
    e?.stopPropagation?.();
    setSortDirection((prev) => {
      if (sortField === 'subscribed') {
        return prev === 'asc' ? 'desc' : 'asc';
      }
      return 'desc';
    });
    setSortField('subscribed');
    setCurrentPage(1);
  }, [sortField]);

  const handleSortByOnboarded = useCallback((e) => {
    e?.stopPropagation?.();
    setSortDirection((prev) => {
      if (sortField === 'onboarded') {
        return prev === 'asc' ? 'desc' : 'asc';
      }
      return 'desc';
    });
    setSortField('onboarded');
    setCurrentPage(1);
  }, [sortField]);

  // --- helper to normalise phone numbers to digits ---
  const digits = (s) => String(s || '').replace(/\D/g, '');

  // Queue-aware "Activate" using backend endpoint
  const handleActivatePort = useCallback(async (phoneNumber) => {
    if (!phoneNumber) return;

    const match = ports.find(
      (p) => digits(p.assigned_number) === digits(phoneNumber)
    );

    if (!match || !match.port_number || !match.id) {
      showToast(`No port mapped to ${phoneNumber}`);
      return;
    }

    // Check current queue status before activating
    try {
      const queueCheck = await api.get(`/api/ports/${match.id}/activation-queue`);
      const existingQueue = queueCheck?.data?.items || [];
      const alreadyQueued = existingQueue.find(
        item => item.target_port_id === match.id && item.status === 'queued'
      );

      if (alreadyQueued) {
        showToast(`⚠ Already in queue for ${phoneNumber}`);
        return;
      }
    } catch (err) {
      // Queue check failed, continue anyway
    }

    try {
      const res = await api.post(`/api/ports/${match.id}/activate`, { number: phoneNumber });
      const info = res?.data || {};
      if (info.status === 'active') {
        showToast(`✓ Activated now: ${phoneNumber} on ${match.port_number}`);
      } else if (info.status === 'already_queued') {
        showToast(`⚠ Already queued: ${phoneNumber}`);
      } else if (info.status === 'queued') {
        const pos = info.position ?? 1;
        const eta = info.eta_seconds ?? 60;
        showToast(`⏳ Queued (position ${pos}) — ETA ~${Math.ceil(eta)}s`);
      }
    } catch (err) {
      console.error('Port activation failed:', err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Failed to enqueue activation';
      showToast(`✗ ${msg}`);
    } finally {
      refreshPortActivation();
    }
  }, [ports, showToast, refreshPortActivation]);

  const getStatusBadge = useCallback(
    (port) => (
      <PortActivationBadge
        statusEntry={portStatus[port?.id]}
        countdown={portCountdowns[port?.id]}
        compact
        showTrafficLight={false}
      />
    ),
    [portStatus, portCountdowns]
  );

  // NEW: when saving a row, also persist proxy to /advice-passwords/{user_code}/{email}/proxy if changed
  const handleSaveEditingRowWithProxy = useCallback(async (entry) => {
    try {
      await saveEditingRow(entry); // this already refetches
      if (editDraft.proxy !== entry.proxy) {
        await saveAdvicePasswordProxy(entry.user_code, entry.email, editDraft.proxy);
      }
      // no extra refetch here
    } catch (error) {
      console.error('Error saving editing row with proxy:', error);
    }
  }, [editDraft, saveEditingRow]);

  // Export: count-first pagination (pageSize 500), one row per password entry (incl. proxy).
  const handleExportToExcel = useCallback(async () => {
    showToast('Preparing export…');
    try {
      const countRes = await api.get('/advice-passwords/count');
      const total = Math.max(0, Number(countRes?.data?.total) ?? 0);

      const pageSize = 500;
      let allPasswords = [];
      let skip = 0;

      while (true) {
        const res = await api.get('/advice-passwords/', {
          params: { skip, limit: pageSize },
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        allPasswords = allPasswords.concat(rows);

        if (rows.length < pageSize || allPasswords.length >= total) break;
        skip += pageSize;
      }

      const exportRows = allPasswords.map((p) => {
        const userCode = p.user_code || 'UNKNOWN';
        const perUser = usersMap[userCode] || {};
        const base = perUser.base || {};
        const emailKey = (p.email || '').trim().toLowerCase();
        const emailData = perUser[emailKey] || {};

        return {
          'User Code': userCode,
          Email: p.email || '',
          Site: p.site || '',
          Password: p.password || '',
          Proxy: p.proxy || '',
          Main: p.main ? 'Yes' : 'No',
          Multilogin: p.multilogin ? 'Yes' : 'No',
          'Full Name': emailData.email_specific_name || base.full_name || '',
          DOB: emailData.email_specific_dob || base.dob || '',
          Address: emailData.email_specific_address || base.address || '',
          Subscribed: base.subscribed ? 'Yes' : 'No',
          Onboarded: base.onboarded ? 'Yes' : 'No',
          'Slack ID': base.slack_user_id || '',
          Referral: base.referral || '',
          Notes: base.notes || '',
        };
      });

      exportRows.sort((a, b) => {
        const cmp = a['User Code'].localeCompare(b['User Code']);
        if (cmp !== 0) return cmp;
        const eCmp = (a.Email || '').localeCompare(b.Email || '');
        if (eCmp !== 0) return eCmp;
        return (a.Site || '').localeCompare(b.Site || '');
      });

      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(exportRows);
      ws['!cols'] = [
        { wch: 12 },
        { wch: 28 },
        { wch: 20 },
        { wch: 22 },
        { wch: 30 },
        { wch: 8 },
        { wch: 10 },
        { wch: 22 },
        { wch: 12 },
        { wch: 30 },
        { wch: 12 },
        { wch: 12 },
        { wch: 18 },
        { wch: 18 },
        { wch: 30 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Advice Passwords');
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `advice-passwords-export-${date}.xlsx`);
      showToast(`✓ Exported ${exportRows.length} entries (${total} total in DB)`);
    } catch (err) {
      console.error('Export failed:', err);
      showToast(`✗ Export failed: ${err?.message || 'Unknown error'}`);
    }
  }, [showToast, usersMap]);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  if (loading && !hasLoadedOnce.current) return <div>Loading...</div>;
  const displayError = editError || bulkError || error;
  if (displayError) {
    return <div className="alert alert-danger py-2">{String(displayError)}</div>;
  }

  return (
    <div className="container-fluid px-3 py-2" style={{ fontSize: '13px' }}>
      <h4 className="mb-3" style={{ fontSize: '18px' }}>
        Advice Password Management
      </h4>

      {(toastMessage || bulkToast) && <Toast message={toastMessage || bulkToast} />}

      {/* Search + “Add New Entries” */}
      <div className="row g-2 mb-2">
        <div className="col-md-4">
          <SearchBar
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v);
              setCurrentPage(1);
            }}
            placeholder="Search user code, full name, email, or Slack ID..."
          />
        </div>
        <div className="col-md-8 d-flex gap-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowNewPasswordForm(true)}
            disabled={showNewPasswordForm}
          >
            Add New Password Entries
          </button>
          <button
            className="btn btn-success btn-sm"
            onClick={handleExportToExcel}
          >
            ⬇ Export to Excel
          </button>
        </div>
      </div>

      {/* Bulk form (must pass the props BulkForm expects) */}
      <BulkForm
        show={showNewPasswordForm}
        onClose={() => {
          setShowNewPasswordForm(false);
          resetNewPasswordForm();
        }}
        entries={newPasswordEntries}
        onFieldChange={handleNewPasswordFieldChange}
        onAddRow={addNewPasswordEntry}
        onRemoveRow={removePasswordEntry}
        onSubmit={handleCreateNewPasswords}
        invalidRows={invalidRows}
        hasValidationIssues={hasValidationIssues}
        sites={sites}
        isStaff={true}
        onRequestSite={submitSiteRequest}
      />

      <SiteRequestsPanel onSitesChanged={handleSitesChanged} />

      {loading && hasLoadedOnce.current && (
        <div className="text-muted mb-2" style={{ fontSize: '12px' }}>
          <span className="spinner-border spinner-border-sm me-1" />
          Searching...
        </div>
      )}

      {/* Main table (no multilogin on this page) */}
      <Table
        entries={paginatedEntries}
        expandedUserCode={expandedUserCode}
        setExpandedUserCode={setExpandedUserCodeWithAutoExpand}
        notesOpenMap={notesOpenMap}
        toggleNotes={toggleNotes}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortSubscribed={handleSortBySubscribed}
        onSortOnboarded={handleSortByOnboarded}
        editingMainRow={editingMainRow}
        mainRowEditDraft={mainRowEditDraft}
        setMainRowEditDraft={setMainRowEditDraft}
        onStartEditingMainRow={startEditingMainRow}
        onSaveMainRow={saveMainRow}
        onCancelMainRow={() => setEditingMainRow(null)}
        withMultilogin={true}
        getStatusBadge={getStatusBadge}
        subscribedCount={subscribedCount}
        childProps={{
          ports: ports,
          users: usersMap,
          autoExpandAll,
          onStartEditingRow: (entry) => startEditingRow(entry, usersMap),
          editingId,
          editDraft,
          onChangeEditField: changeEditField,
          onCancelEditingRow: cancelEditingRow,
          onSaveEditingRow: (entry) => handleSaveEditingRowWithProxy(entry),
          onSaveAndApplyAll: (entry) => saveAndApplyAll(entry),
          showAddressEditor,
          setShowAddressEditor,
          addressParts,
          setAddressParts,
          parseAddressIntoParts,
          handleAddressSave,
          addingNewRecord,
          setAddingNewRecord,
          newRecordUserCode,
          setNewRecordUserCode,
          newRecordDraft,
          setNewRecordDraft,
          onCreateNewRecord: createNewRecord,
          sites: sites,
          isStaff: true,
          onDeleteRecord: deleteRecord,
          onSaveNotes: saveNotes,
          onActivatePort: handleActivatePort,
          refetch: refetch, // Pass refetch to ChildRows
          onToggleMultilogin: handleToggleMultilogin,
          multiloginOverrides,
          onToggleMlEnvironment: handleToggleMlEnvironment,
          mlEnvironmentOverrides,
          onRequestSite: submitSiteRequest,
          showPersonaSelfTransfer: true,
          onPersonaCheckSubmit: handlePersonaCheckSubmit,
          onPersonaCheckClear: handlePersonaCheckClear,
          onSelfTransferToggle: handleSelfTransferToggle,
          personaSelfOverrides,
        }}
      />

      {/* Pagination footer - server-side total */}
      {totalCount > itemsPerPage && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted" style={{ fontSize: '12px' }}>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
            {totalCount} entries
          </div>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                  First
                </button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((c) => Math.max(c - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {Array.from({ length: Math.ceil(totalCount / itemsPerPage) }).map((_, i) => {
                const pageNumber = i + 1;
                return (
                  <li
                    key={pageNumber}
                    className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                  >
                    <button className="page-link" onClick={() => setCurrentPage(pageNumber)}>
                      {pageNumber}
                    </button>
                  </li>
                );
              })}
              <li
                className={`page-item ${
                  currentPage === Math.ceil(totalCount / itemsPerPage) || totalCount === 0
                    ? 'disabled'
                    : ''
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage((c) =>
                      Math.min(c + 1, Math.ceil(totalCount / itemsPerPage))
                    )
                  }
                  disabled={
                    currentPage === Math.ceil(totalCount / itemsPerPage) || totalCount === 0
                  }
                >
                  Next
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === Math.ceil(totalCount / itemsPerPage) || totalCount === 0
                    ? 'disabled'
                    : ''
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(Math.ceil(totalCount / itemsPerPage))}
                  disabled={
                    currentPage === Math.ceil(totalCount / itemsPerPage) || totalCount === 0
                  }
                >
                  Last
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

export default AdvicePasswordManagement;
